import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { theme } from '../theme';
import { BentoCard } from './BentoCard';
import { Spinner } from './Spinner';
import { listRoutingRules, upsertRoutingRule, deleteRoutingRule } from '../services/api';
import { RoutingRule, RoutingRuleUpsert } from '../serverTypes';
import { formatBackendError } from '../utils/formatError';

// <purpose>Routing tab — routing_rules CRUD. A rule classifies an inbound mail into a named business
// case (triggerBeschreibung + triggerBeispiele) and tells the clerk how to proceed (internalText →
// "Routing: an X weiterleiten"). answerDirective (optional free text) is handed to the answer LLM to
// steer/force the customer reply ("Zwischeninfo an Kunden"). All rows are tenant-owned; admin adds a
// rule during operation → reanalyze → the mail routes, no deploy (the demo beat).</purpose>
// <dependencies>GET/POST /manage/routing (listRoutingRules/upsertRoutingRule), DELETE
// /manage/routing/{ruleId} (deleteRoutingRule) — services/api.ts. BentoCard.tsx/Spinner.tsx.
// rule_id server contract: ^[a-z0-9_]+$ (mirrored client-side as a UX nicety; server stays
// authoritative).</dependencies>
// <constraints>Mirrors AntwortKatalog's layout-stability discipline: the thead/column-frame renders
// unconditionally, only tbody rows swap; the form lives in its own section below the table so the
// table never jumps. Two feedback homes, one concern each: the top banner is list-level only (load
// errors, delete feedback); save-related feedback (validation, save error, save success) renders in
// a pre-allocated line directly above the button row, in-view by construction — no scroll needed.</constraints>

const thStyle: React.CSSProperties = { padding: '8px 12px', color: theme.colors.textSecondary, fontWeight: 500, textAlign: 'left' };
const tdStyle: React.CSSProperties = { padding: '10px 12px', fontSize: 13 };
const inputStyle: React.CSSProperties = {
  padding: '8px 10px', borderRadius: 6, border: `1px solid ${theme.colors.bgLight}`,
  fontSize: 13, fontFamily: 'inherit', width: '100%', boxSizing: 'border-box',
};
const labelStyle: React.CSSProperties = {
  fontSize: 12, fontWeight: 600, color: theme.colors.navyMid, marginBottom: 4, display: 'block',
};
function btnStyle(kind: 'primary' | 'danger' | 'ghost'): React.CSSProperties {
  const base: React.CSSProperties = {
    padding: '7px 14px', borderRadius: 6, fontSize: 13, cursor: 'pointer',
    border: '1px solid transparent', fontWeight: 500,
  };
  if (kind === 'primary') return { ...base, background: theme.colors.navy, color: theme.colors.white };
  // Full `border` (not `borderColor`) — mixing shorthand + longhand for the same property across a
  // rerender (danger ⇄ ghost swap) triggers a React DOM styling warning (AntwortKatalog precedent).
  if (kind === 'danger') return { ...base, background: theme.colors.white, color: theme.colors.error, border: '1px solid #e0b4b4' };
  return { ...base, background: theme.colors.bgLight, color: theme.colors.navyMid };
}
function feedbackStyle(isError: boolean): React.CSSProperties {
  return {
    padding: '8px 12px', borderRadius: theme.borderRadius.sm, fontSize: 13,
    background: isError ? '#fbe9e9' : '#e9f6ec', color: isError ? theme.colors.error : '#1f7a3d',
  };
}

const linesToArr = (s: string): string[] => s.split('\n').map(x => x.trim()).filter(Boolean);
const arrToLines = (a: string[]): string => (a ?? []).join('\n');

const emptyForm: RoutingRuleUpsert = {
  ruleId: '', name: '', triggerBeschreibung: '', triggerBeispiele: [],
  internalText: '', erklaerung: null, answerDirective: null, targetUnit: null, prioritaet: 100, aktiv: true,
};

const RoutingVerwaltung: React.FC = () => {
  const [rules, setRules] = useState<RoutingRule[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [form, setForm] = useState<RoutingRuleUpsert>(emptyForm);
  const [triggerText, setTriggerText] = useState('');
  const [editingRuleId, setEditingRuleId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  // Save-scoped feedback (validation, save error, save success) — rendered beside "Regel anlegen",
  // not in the top banner (that's list-level only). See routing-save-feedback below.
  const [saveError, setSaveError] = useState('');
  const [saveSuccess, setSaveSuccess] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      setRules(await listRoutingRules());
    } catch (e) {
      setError(formatBackendError(e, 'Laden der Routing-Regeln'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const resetForm = () => {
    setForm(emptyForm); setTriggerText('');
    setEditingRuleId(null); setSaveError(''); setSaveSuccess('');
  };

  const startEdit = (r: RoutingRule) => {
    setEditingRuleId(r.ruleId);
    setSaveError(''); setSaveSuccess('');
    setForm({
      ruleId: r.ruleId, name: r.name, triggerBeschreibung: r.triggerBeschreibung,
      triggerBeispiele: r.triggerBeispiele, internalText: r.internalText,
      erklaerung: r.erklaerung, answerDirective: r.answerDirective, targetUnit: r.targetUnit,
      prioritaet: r.prioritaet, aktiv: r.aktiv,
    });
    setTriggerText(arrToLines(r.triggerBeispiele));
  };

  const validationError = useMemo(() => {
    const trimmed = form.ruleId.trim();
    if (!trimmed) return 'rule_id ist erforderlich.';
    if (!/^[a-z0-9_]+$/.test(trimmed)) return 'rule_id: nur Kleinbuchstaben, Ziffern, Unterstrich.';
    if (!editingRuleId && rules.some(r => r.ruleId === trimmed))
      return 'Diese rule_id existiert bereits — zum Bearbeiten die Zeile verwenden.';
    if (!form.name.trim()) return 'Name ist erforderlich.';
    if (!form.triggerBeschreibung.trim()) return 'Beschreibung des Falls ist erforderlich.';
    if (!form.internalText.trim()) return 'Interner Text (Weiterleitung) ist erforderlich.';
    return '';
  }, [form, editingRuleId, rules]);

  const handleSave = async () => {
    if (validationError) { setSaveError(validationError); return; }
    setSaving(true); setSaveError(''); setSaveSuccess('');
    try {
      const directive = (form.answerDirective ?? '').trim();
      const target = (form.targetUnit ?? '').trim();
      const erklaerung = (form.erklaerung ?? '').trim();
      await upsertRoutingRule({
        ...form,
        ruleId: form.ruleId.trim(),
        name: form.name.trim(),
        triggerBeschreibung: form.triggerBeschreibung.trim(),
        triggerBeispiele: linesToArr(triggerText),
        internalText: form.internalText.trim(),
        erklaerung: erklaerung || null,
        answerDirective: directive || null,
        targetUnit: target || null,
      });
      const message = editingRuleId ? `Regel „${form.ruleId}" aktualisiert.` : `Regel „${form.ruleId}" angelegt.`;
      // resetForm() clears saveSuccess along with the form fields — set the success message AFTER
      // so it isn't clobbered by that clear (both calls land in the same tick/batch).
      resetForm();
      setSaveSuccess(message);
      await load();
    } catch (e) {
      setSaveError(formatBackendError(e, 'Speichern der Routing-Regel'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (ruleId: string) => {
    setDeleting(ruleId); setError(''); setSuccess('');
    try {
      await deleteRoutingRule(ruleId);
      setSuccess(`Regel „${ruleId}" gelöscht.`);
      setDeleteConfirm(null);
      if (editingRuleId === ruleId) resetForm();
      await load();
    } catch (e) {
      setError(formatBackendError(e, 'Löschen der Routing-Regel'));
    } finally {
      setDeleting(null);
    }
  };

  const set = <K extends keyof RoutingRuleUpsert>(k: K, v: RoutingRuleUpsert[K]) =>
    setForm(f => ({ ...f, [k]: v }));

  return (
    <BentoCard title="Routing">
      <div style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing.md }}>
        <p style={{ fontSize: 13, color: theme.colors.textSecondary, margin: 0, lineHeight: 1.5 }}>
          Eine Routing-Regel erkennt einen Geschäftsfall in eingehenden Mails und schlägt dem
          Sachbearbeiter vor, wie zu verfahren ist. „Antwort an Kunden" (optional) weist die KI an,
          eine passende Kundenantwort zu formulieren (z.B. „Zwischeninfo an Kunden") — leer lassen für
          reine interne Weiterleitung.
        </p>

        {/* Fixed-height, always-rendered — a message appearing/clearing swaps content inside this
            box rather than mounting/unmounting an element, so nothing below it ever jumps. */}
        <div data-testid="routing-feedback" style={{ minHeight: 40, display: 'flex', alignItems: 'stretch' }}>
          {error ? (
            <div style={{ ...feedbackStyle(true), width: '100%', boxSizing: 'border-box' }}>{error}</div>
          ) : success ? (
            <div style={{ ...feedbackStyle(false), width: '100%', boxSizing: 'border-box' }}>{success}</div>
          ) : null}
        </div>

        {/* thead/column-frame is unconditional — only tbody rows swap on loading/empty/data. */}
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ borderBottom: `2px solid ${theme.colors.bgLight}` }}>
              <th style={thStyle}>Fall</th>
              <th style={thStyle}>Aussteuerung</th>
              <th style={{ ...thStyle, width: 120 }}>Antwort?</th>
              <th style={{ ...thStyle, width: 70 }}>Aktiv</th>
              <th style={{ ...thStyle, width: 160 }}>Aktionen</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={5} style={{ padding: '24px 12px', textAlign: 'center' }}>
                <Spinner size={22} label="Lade Regeln…" />
              </td></tr>
            )}
            {!loading && rules.length === 0 && (
              <tr><td colSpan={5} style={{ padding: '20px 12px', color: theme.colors.textSecondary, textAlign: 'center' }}>
                Noch keine Routing-Regel angelegt.
              </td></tr>
            )}
            {!loading && rules.map(r => (
              <tr key={r.ruleId} style={{ borderBottom: `1px solid ${theme.colors.bgLight}` }}>
                <td style={tdStyle}>{r.name}</td>
                <td style={tdStyle}>{r.internalText}{r.targetUnit ? ` (${r.targetUnit})` : ''}</td>
                <td style={{ ...tdStyle, textAlign: 'center' }}>{r.answerDirective ? 'ja' : '—'}</td>
                <td style={{ ...tdStyle, textAlign: 'center' }}>
                  <span
                    title={r.aktiv ? 'Aktiv' : 'Inaktiv'}
                    style={{
                      display: 'inline-block', width: 8, height: 8, borderRadius: '50%',
                      backgroundColor: r.aktiv ? theme.colors.success : theme.colors.borderLight,
                    }}
                  />
                </td>
                <td style={tdStyle}>
                  {deleteConfirm === r.ruleId ? (
                    <div style={{ display: 'flex', gap: 4 }}>
                      <button onClick={() => handleDelete(r.ruleId)} style={btnStyle('danger')} disabled={deleting === r.ruleId}>
                        {deleting === r.ruleId ? 'Löscht…' : 'Löschen'}
                      </button>
                      <button onClick={() => setDeleteConfirm(null)} style={btnStyle('ghost')}>Abbrechen</button>
                    </div>
                  ) : (
                    <div style={{ display: 'flex', gap: 4 }}>
                      <button onClick={() => startEdit(r)} style={btnStyle('ghost')}>Bearbeiten</button>
                      <button onClick={() => setDeleteConfirm(r.ruleId)} style={btnStyle('ghost')}>Löschen</button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* ── Formular: eigener Abschnitt unter der Tabelle, damit die Tabelle nie springt. ── */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing.md, borderTop: `1px solid ${theme.colors.bgLight}`, paddingTop: theme.spacing.md }}>
          <span style={{ fontSize: 14, fontWeight: 600, color: theme.colors.navy }}>
            {editingRuleId ? `Regel bearbeiten: ${editingRuleId}` : 'Neue Routing-Regel anlegen'}
          </span>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: theme.spacing.md }}>
            <div>
              <label htmlFor="rule-id" style={labelStyle}>rule_id (Slug)</label>
              <input id="rule-id" style={{ ...inputStyle, opacity: editingRuleId ? 0.6 : 1 }}
                value={form.ruleId} disabled={!!editingRuleId}
                placeholder="z.B. doppelbelieferung"
                onChange={ev => set('ruleId', ev.target.value)} />
            </div>
            <div>
              <label htmlFor="rule-name" style={labelStyle}>Name</label>
              <input id="rule-name" style={inputStyle} value={form.name}
                placeholder="z.B. Doppelbelieferung"
                onChange={ev => set('name', ev.target.value)} />
            </div>
          </div>

          <div>
            <label htmlFor="rule-beschreibung" style={labelStyle}>Fall-Beschreibung (nutzt die KI zum Erkennen)</label>
            <textarea id="rule-beschreibung" style={{ ...inputStyle, minHeight: 64, resize: 'vertical' }}
              value={form.triggerBeschreibung}
              placeholder="Worum geht es? z.B. Ein Kunde wird zeitgleich von zwei Stromlieferanten beliefert."
              onChange={ev => set('triggerBeschreibung', ev.target.value)} />
          </div>

          <div>
            <label htmlFor="rule-trigger" style={labelStyle}>Passt bei (ein Beispiel pro Zeile, optional)</label>
            <textarea id="rule-trigger" style={{ ...inputStyle, minHeight: 60, resize: 'vertical' }}
              value={triggerText} placeholder={'Ich werde doppelt beliefert\nzwei Stromanbieter gleichzeitig'}
              onChange={ev => setTriggerText(ev.target.value)} />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: theme.spacing.md }}>
            <div>
              <label htmlFor="rule-internal" style={labelStyle}>Weiterleitung / interner Text (an SB)</label>
              <input id="rule-internal" style={inputStyle} value={form.internalText}
                placeholder="z.B. Aussteuern an Abteilung Clearing"
                onChange={ev => set('internalText', ev.target.value)} />
            </div>
            <div>
              <label htmlFor="rule-target" style={labelStyle}>Ziel-Abteilung (optional)</label>
              <input id="rule-target" style={inputStyle} value={form.targetUnit ?? ''}
                placeholder="z.B. Clearing"
                onChange={ev => set('targetUnit', ev.target.value || null)} />
            </div>
          </div>

          <div>
            <label htmlFor="rule-erklaerung" style={labelStyle}>Erklärung für den Sachbearbeiter (optional — interner Kontext zur Regel)</label>
            <textarea id="rule-erklaerung" style={{ ...inputStyle, minHeight: 56, resize: 'vertical' }}
              value={form.erklaerung ?? ''}
              placeholder="z.B. Klärung der Zuordnungsvereinbarung nötig; Kunde nicht doppelt anschreiben."
              onChange={ev => set('erklaerung', ev.target.value || null)} />
          </div>

          <div>
            <label htmlFor="rule-directive" style={labelStyle}>Antwort an Kunden (optional — Weisung an die KI)</label>
            {/* Fix loop pass 2, item 3 — the only single-line field on this form that isn't already
                capped by a 2-column grid (rule-id/rule-name, rule-internal/rule-target): at the
                widened 1200px main, inputStyle's own width: '100%' stretched a one-phrase directive
                across the full ~1072px card, reading as an oversized field for what it holds. Capped
                locally, not by narrowing the page — 600px comfortably fits the placeholder's own
                example phrase with room to spare, without ballooning into a full-width strip. */}
            <input id="rule-directive" style={{ ...inputStyle, maxWidth: 600 }} value={form.answerDirective ?? ''}
              placeholder='z.B. "Zwischeninfo an Kunden" — leer = reine interne Weiterleitung'
              onChange={ev => set('answerDirective', ev.target.value || null)} />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: theme.spacing.md, alignItems: 'end' }}>
            <div>
              <label htmlFor="rule-prio" style={labelStyle}>Priorität</label>
              <input id="rule-prio" type="number" style={inputStyle} value={form.prioritaet}
                onChange={ev => set('prioritaet', Number(ev.target.value) || 0)} />
            </div>
            <label style={{ fontSize: 12, display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer', paddingBottom: 8 }}>
              <input type="checkbox" checked={form.aktiv} onChange={ev => set('aktiv', ev.target.checked)} />
              Aktiv
            </label>
          </div>

          {/* Pre-allocated, in-view by construction — save feedback (validation, save error, save
              success) lives beside the button it reacts to, not in the list-level banner above.
              minHeight holds the space so the button row below never shifts when a message
              appears or clears; the message itself fades in per the stx-fadeIn idiom. */}
          <div data-testid="routing-save-feedback" style={{ minHeight: 40, display: 'flex', alignItems: 'stretch' }}>
            {saveError ? (
              <div style={{ ...feedbackStyle(true), width: '100%', boxSizing: 'border-box', animation: 'stx-fadeIn 0.2s ease' }}>{saveError}</div>
            ) : saveSuccess ? (
              <div style={{ ...feedbackStyle(false), width: '100%', boxSizing: 'border-box', animation: 'stx-fadeIn 0.2s ease' }}>{saveSuccess}</div>
            ) : null}
          </div>

          <div style={{ display: 'flex', gap: theme.spacing.sm, alignItems: 'center' }}>
            <div style={{ flex: 1 }} />
            {editingRuleId && <button onClick={resetForm} style={btnStyle('ghost')}>Abbrechen</button>}
            <button onClick={handleSave} style={btnStyle('primary')} disabled={saving}>
              {saving ? 'Speichern…' : editingRuleId ? 'Regel aktualisieren' : 'Regel anlegen'}
            </button>
          </div>
        </div>
      </div>
    </BentoCard>
  );
};

export default RoutingVerwaltung;
